/*
 * NAO-97 T12: analytics transport (BUILD-PLAN 1.2, 5, 6.1; D8).
 *
 * Defines window.NaomiPDPTrack(event, props), which assets/naomi-pdp.js#track calls
 * with the common properties (handle, store, template, mobile_cta,
 * offers_position, variant_id, viewport) already merged into props. This file adds
 * nothing to that contract; it is only the transport.
 *
 * Does nothing at all (no listeners, no observers, no network) until
 * window.NaomiPostHog is present, which layout/theme.liquid outputs only when
 * settings.naomi_posthog_key is set. Loading posthog-js and the consent gate live in
 * assets/naomi-posthog.js (site-wide); this file sends through that instance and drops
 * events until it sets window.NaomiPostHog.ready, so nothing is queued before consent.
 *
 * Also owns the scroll events T12 fires itself, through NaomiPDP.track so they
 * carry the same common properties as every other event. Their observers start only once
 * posthog is ready (NaomiPostHog.whenReady), so a chapter already on screen by then still
 * counts (an observer reports its targets' state when it starts), instead of being marked
 * seen while sends are still dropped:
 *   chapter_viewed  once per [data-pdp-chapter], the first time it is 50% visible
 *                   (or fills half the viewport, for chapters taller than the screen);
 *                   also for the [data-pdp-seen] note sections (care, custom), index -1,
 *                   which stay out of the chapter order and stack_depth
 *   stack_depth     the deepest chapter reached, sent on pagehide
 *   pdp_buybox_reached  phones only, once: the buy box price block half on screen in the top
 *                   three quarters of it (clear of the peek drawer, which draws its own copy
 *                   over the buy box top): product_id, photo_count (stack photos shown).
 */
(() => {
  const config = window.NaomiPostHog;
  if (!config || !config.key) return; // no key configured: stay a no-op (BUILD-PLAN T12)

  window.NaomiPDPTrack = (event, props) => {
    if (!config.ready) return; // dropped, never queued past a session with no consent
    window.posthog.capture(event, props);
  };

  if (!('IntersectionObserver' in window) || !config.whenReady) return;
  const loadStart = Date.now();

  // pdp_buybox_reached: the price container is never replaced (product-info.js swaps its contents)
  const price = document.querySelector('[data-pdp-buy] [id^="price-"]');
  const phone = window.matchMedia('(max-width: 749px)');
  if (price) {
    const reached = new IntersectionObserver(
      (entries) => {
        if (!phone.matches || !entries.some((entry) => entry.intersectionRatio >= 0.5)) return;
        reached.disconnect();
        const PDP = window.NaomiPDP;
        PDP?.track('pdp_buybox_reached', {
          product_id: PDP.product.id,
          photo_count: Array.from(document.querySelectorAll('.pdp-gallery__stack > .product__media-item')).filter(
            (item) => item.offsetParent !== null
          ).length,
          ms_since_load: Date.now() - loadStart,
        });
      },
      { threshold: 0.5, rootMargin: '0px 0px -25% 0px' }
    );
    config.whenReady(() => reached.observe(price));
  }

  // chapter_viewed / stack_depth
  const chapters = Array.from(document.querySelectorAll('[data-pdp-chapter]'));
  if (!chapters.length) return;

  const order = chapters.map((el) => el.dataset.pdpChapter);
  const seen = new Set();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const chapter = entry.target.dataset.pdpChapter || entry.target.dataset.pdpSeen;
        // a chapter taller than 2x the viewport can never be 50% visible: half a screen of it counts too
        const seenEnough = entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight / 2;
        if (!entry.isIntersecting || !seenEnough || seen.has(chapter)) return;
        seen.add(chapter);
        observer.unobserve(entry.target);
        window.NaomiPDP?.track('chapter_viewed', {
          chapter,
          index: order.indexOf(chapter),
          ms_since_load: Date.now() - loadStart,
        });
      });
    },
    // A callback only runs when a threshold is crossed, so a chapter that tops out at 20% of its own
    // height (3500px in a 720px viewport) needs thresholds below 0.5 to ever be re-checked.
    // 0.02 steps are finer than half a screen for chapters up to ~18000px.
    { threshold: Array.from({ length: 51 }, (_, i) => i / 50) }
  );
  config.whenReady(() => {
    chapters.forEach((el) => observer.observe(el));
    document.querySelectorAll('[data-pdp-seen]').forEach((el) => observer.observe(el));
  });

  window.addEventListener('pagehide', () => {
    if (!seen.size) return;
    const maxIndex = Math.max(...Array.from(seen, (chapter) => order.indexOf(chapter)));
    if (maxIndex < 0) return; // only note sections seen
    window.NaomiPDP?.track('stack_depth', {
      max_chapter: order[maxIndex],
      max_pct: Math.round(((maxIndex + 1) / order.length) * 100),
    });
  });
})();
